import type { AudioVisualFrame } from '../domain/types'
import { PENTATONIC_INTERVALS } from '../music/thereminMapper'
const NOTE_COLORS=['#f28c82','#f6b26b','#f5d66f','#c8de79','#7bd8a7','#69d8d2','#78c8f4','#99adfa','#bb9bec','#df99d7','#f1a6bc'] as const
export const NOTE_STRING_STYLES=PENTATONIC_INTERVALS.map((interval,index)=>({color:NOTE_COLORS[index]!,rate:.012*2**(interval/12),waveNumber:50+interval*2,speed:.34+interval*.01}))
export interface StringHand { slot:number; note:number; x:number; span:number; gate:boolean }
interface HeldString extends StringHand { anchor:number; lastSpan:number; at:number; lastPulse:number }
interface Pulse { note:number; x:number; at:number; strength:number; slot:number }
/** Normalized string displacement. Rendering and audio share the mapper's note index. */
export class NoteStrings {
  private held=new Map<number,HeldString>()
  private pulses:Pulse[]=[]
  private audioNotes = new Float32Array(PENTATONIC_INTERVALS.length)
  private audioAt = 0
  brightness = 0
  setAudio(frame: AudioVisualFrame | undefined, at: number) {
    const dt = this.audioAt ? Math.max(0, Math.min(250, at - this.audioAt)) : 16
    this.audioAt = at
    for (let note = 0; note < this.audioNotes.length; note++) {
      const value = frame?.notes[note] ?? 0
      const target = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0
      const current = this.audioNotes[note]!
      this.audioNotes[note] = current + (target - current) * (1 - Math.exp(-dt / (target > current ? 45 : 180)))
    }
    this.brightness += ((frame?.brightness ?? 0) - this.brightness) * (1 - Math.exp(-dt / 100))
  }
  energy(note: number) { return this.audioNotes[note] ?? 0 }
  update(hands:readonly StringHand[],at:number){
    const live=new Set(hands.map(h=>h.slot))
    for(const slot of this.held.keys())if(!live.has(slot))this.held.delete(slot)
    for(const hand of hands){
      const previous=this.held.get(hand.slot)
      if(!previous||previous.note!==hand.note||at-previous.at>250){
        this.held.set(hand.slot,{...hand,anchor:hand.span,lastSpan:hand.span,at,lastPulse:-Infinity});continue
      }
      const dt=Math.max(1,at-previous.at),ratio=hand.span/Math.max(.001,previous.anchor)
      const speed=(hand.span-previous.lastSpan)/Math.max(.001,previous.anchor)*1000/dt
      if(hand.span<previous.anchor)previous.anchor=hand.span
      if(ratio>1.085&&speed>.12&&at-previous.lastPulse>450){
        this.pulses.push({note:hand.note,x:hand.x,at,strength:Math.min(1,(ratio-1)*4+.25),slot:hand.slot})
        previous.anchor=hand.span;previous.lastPulse=at
      }
      Object.assign(previous,hand,{lastSpan:hand.span,at})
    }
    this.expire(at)
    if(this.pulses.length>16)this.pulses.splice(0,this.pulses.length-16)
  }
  sample(note:number,x:number,at:number,reducedMotion=false,phaseOffset=0):number{
    if(reducedMotion)return 0
    const style=NOTE_STRING_STYLES[note]??NOTE_STRING_STYLES[0]!
    const energy = this.energy(note)
    const phase = at * style.rate * .65 + phaseOffset
    // The broad wave exists only when the output analyser measures this pitch band.
    let displacement = energy > .002 ? (.08 * Math.pow(energy, .65)) * (
      Math.sin(x * style.waveNumber * .56 - phase) +
      .3 * Math.sin(x * style.waveNumber * (1.05 + this.brightness * .3) + phase * .7)
    ) : 0
    for(const hand of this.held.values())if(hand.note===note&&at-hand.at<250){
      const dx=x-hand.x,envelope=Math.exp(-dx*dx/.012)
      displacement+=Math.sin(phase+dx*style.waveNumber)*envelope*(hand.gate?.012:.003)
    }
    for(const pulse of this.pulses)if(pulse.note===note){
      const age=(at-pulse.at)/1000
      if(age<0||age>1.8)continue
      const phase=Math.abs(x-pulse.x)-age*style.speed,envelope=Math.exp(-phase*phase/.0025)*Math.exp(-age*1.6)
      displacement+=Math.sin(phase*style.waveNumber)*envelope*pulse.strength*.065
    }
    return Math.max(-.055,Math.min(.055,displacement))*Math.sin(Math.PI*Math.max(0,Math.min(1,x)))
  }
  activity(note:number,at:number){return this.energy(note)>.012||[...this.held.values()].some(h=>h.note===note&&at-h.at<250)||this.pulses.some(p=>p.note===note&&at-p.at<1800)}
  get pulseCount(){return this.pulses.length}
  expire(at:number){this.pulses=this.pulses.filter(p=>at-p.at<1800)}
  reset(){this.held.clear();this.pulses=[];this.audioNotes.fill(0);this.audioAt=0;this.brightness=0}
}
